import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { themeClasses, Button, useTheme } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';
import { AdminViewBanner } from '@/components/AdminViewBanner';
import { useSiteChrome } from '@/hooks/useSiteChrome';

const navigation = [
  { name: 'About Us', href: '/about', external: false },
  { name: 'Collection', href: '/collection', external: false },
  { name: 'INSIDER Magazine', href: '/insider', external: false },
];

// Pages where navbar should hide on scroll
const hideOnScrollPages = ['/collection'];

// Paid social / connect landing pages: no Login / Advisor Sign Up in header
const pathsHideHeaderAuth = ['/connect', '/partner-connect', '/advisor-connect'];

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lastScrollY = useRef(0);
  const { isLightTheme } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  useSiteChrome();

  const isActive = (path: string) => location.pathname === path;
  const shouldHideOnScroll = hideOnScrollPages.includes(location.pathname);
  const hideHeaderAuth = pathsHideHeaderAuth.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrolled(currentScrollY > 10);

      // Only hide on specific pages
      if (shouldHideOnScroll) {
        const heroHeight = 200;

        if (currentScrollY > heroHeight) {
          // Past the hero - check scroll direction
          if (currentScrollY > lastScrollY.current + 10) {
            setIsHidden(true);
          } else if (currentScrollY < lastScrollY.current - 10) {
            setIsHidden(false);
          }
        } else {
          setIsHidden(false);
        }
      } else {
        setIsHidden(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shouldHideOnScroll]);

  // Reset hidden state when changing pages
  useEffect(() => {
    setIsHidden(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 border-b border-theme-border transition-all duration-300 bg-white/80 backdrop-blur-md ${
        isHidden ? '-top-[var(--fhxd-chrome-height)]' : 'top-0'
      }`}
    >
      <AdminViewBanner />
      <nav className={themeClasses.container}>
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={isLightTheme ? '/images/logos/fhxd-logo-black.png' : '/images/logos/fhxd-logo-white.png'}
              alt="Four Hundred by Design"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${themeClasses.text.button} text-sm transition-colors duration-300 text-theme-text-muted hover:text-theme-text`}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${themeClasses.text.button} text-sm transition-colors duration-300 ${isActive(item.href)
                    ? 'text-theme-text'
                    : 'text-theme-text-muted hover:text-theme-text'
                    }`}
                >
                  {item.name}
                </Link>
              )
            ))}

            {/* Auth buttons */}
            {isAuthenticated ? (
              <Button variant="secondary" onClick={logout}>
                Log Out
              </Button>
            ) : !hideHeaderAuth ? (
              <>
                <Link
                  to="/login"
                  className={`${themeClasses.text.button} text-sm text-theme-text-muted hover:text-theme-text transition-colors duration-300`}
                >
                  Login
                </Link>
                <Button variant="primary" onClick={() => navigate('/signup')}>
                  Advisor Sign Up
                </Button>
              </>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 text-theme-text-muted hover:text-theme-text"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-theme-border">
            <div className="space-y-4">
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block ${themeClasses.text.button} text-sm text-theme-text-muted`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block ${themeClasses.text.button} text-sm ${isActive(item.href)
                      ? 'text-theme-text'
                      : 'text-theme-text-muted'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              {(isAuthenticated || !hideHeaderAuth) && (
                <div className="pt-4 border-t border-theme-border space-y-4">
                  {isAuthenticated ? (
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        logout();
                      }}
                    >
                      Log Out
                    </Button>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className={`block ${themeClasses.text.button} text-sm text-theme-text-muted`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate('/signup');
                        }}
                      >
                        Advisor Sign Up
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export default Header;
